/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Badge,
  ListGroup,
} from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaPlus, FaExternalLinkAlt } from 'react-icons/fa';
import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import SearchableSelect from '../../components/SearchableSelect';
import RequesterModal from '../../components/RequesterModal';
import BlockedAlert from '../../components/BlockedAlert';

export default function TicketsAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const notification = useNotification();

  // Dados vindos do aviso de atendimento assumido no chat. Capturado uma única
  // vez: navegar dentro da tela depois não deve reaplicar o preenchimento.
  const [chatPrefill] = useState(
    () => (location.state && location.state.chatPrefill) || null
  );
  // Garante que a mesa só é escolhida automaticamente uma vez: depois disso a
  // seleção é do agente, e não pode ser desfeita por uma nova renderização.
  const mesaPrefillAplicada = useRef(false);

  // --- Estados do Componente ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Estados para o formulário
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRequesterId, setSelectedRequesterId] = useState('');
  const [priority, setPriority] = useState('');
  const [stages, setStages] = useState('');
  const [serviceDesk, setServiceDesk] = useState('');
  const [status, setStatus] = useState('aberto');
  const [internalCommunication, setInternalCommunication] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [title, setTitle] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [files, setFiles] = useState([]);
  const [origem, setOrigem] = useState('');
  const [origensOptions, setOrigensOptions] = useState([]);
  const [protocolNumber, setProtocolNumber] = useState('');

  // Estados para popular os dropdowns
  const [serviceDesks, setServiceDesks] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [filteredRequesters, setFilteredRequesters] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredPriorities, setFilteredPriorities] = useState([]);
  const [filteredStages, setFilteredStages] = useState([]);

  // Estados para o modal de solicitante
  const [showRequesterModal, setShowRequesterModal] = useState(false);

  // ✅ NOVOS ESTADOS PARA O PAINEL LATERAL DE TICKETS
  const [customerTickets, setCustomerTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [selectedCompanyTags, setSelectedCompanyTags] = useState([]);

  const internalQuillRef = useRef();
  const fileInputRef = useRef();

  // ✅ Estado para alerta de cliente bloqueado
  const [isCustomerBlocked, setIsCustomerBlocked] = useState(false);
  const [blockedCheckError, setBlockedCheckError] = useState(null);

  // Validação de email do solicitante
  const [emailValidation, setEmailValidation] = useState({
    status: 'idle',
    message: '',
  });

  const hasBlockedTag = (tags) => {
    if (!Array.isArray(tags)) return false;
    return tags.some((t) => {
      const name = (t?.name || '').toLowerCase();
      return name.includes('bloqueado');
    });
  };

  const checkCustomerBlocked = async (customerId) => {
    setBlockedCheckError(null);
    setIsCustomerBlocked(false);
    if (!customerId) return;
    try {
      const { data } = await axios.get(`/customers/${customerId}`);
      const blocked = hasBlockedTag(data?.tags || []);
      setSelectedCompanyTags(Array.isArray(data?.tags) ? data.tags : []);
      setIsCustomerBlocked(blocked);
      if (blocked) {
        notification(
          'Esse cliente encontra-se bloqueado, por favor verifique com o financeiro',
          'warning'
        );
      }
    } catch (err) {
      setBlockedCheckError(err);
      setIsCustomerBlocked(false);
      setSelectedCompanyTags([]);
    }
  };

  // ✅ FUNÇÃO PARA BUSCAR TICKETS DO CLIENTE
  const fetchCustomerTickets = async (customerId) => {
    if (!customerId) {
      setCustomerTickets([]);
      return;
    }

    console.log('🔍 Buscando tickets para customerId:', customerId);
    setLoadingTickets(true);
    try {
      const { data } = await axios.get('/relatorios/search-tickets', {
        params: {
          customerId,
          limit: 5, // Limitado para apenas 5 tickets
        },
      });
      console.log('📋 Resposta completa da API:', data);
      console.log('📊 Tickets recebidos:', data.tickets);
      console.log('📊 Quantidade de tickets:', data.tickets?.length || 0);

      // Ordenar por data de criação (mais recentes primeiro) e pegar apenas os 5 primeiros
      const sortedTickets = Array.isArray(data.tickets)
        ? data.tickets
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5)
        : [];

      console.log('✅ Tickets ordenados e limitados:', sortedTickets);
      setCustomerTickets(sortedTickets);
    } catch (err) {
      console.error('❌ Erro ao buscar tickets do cliente:', err);
      setCustomerTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  // ✅ FUNÇÃO PARA AGRUPAR TICKETS POR STATUS
  const groupTicketsByStatus = () => {
    const groups = {
      aberto: [],
      em_atendimento: [],
      pausado: [],
      fechado: [],
      cancelado: [], // Adicionado suporte ao status cancelado
    };

    // Verificar se customerTickets é um array antes de usar forEach
    if (Array.isArray(customerTickets)) {
      customerTickets.forEach((ticket) => {
        if (groups[ticket.status]) {
          groups[ticket.status].push(ticket);
        }
      });
    }

    return groups;
  };

  // ✅ FUNÇÃO PARA OBTER COR DO BADGE POR STATUS
  const getStatusBadgeVariant = (ticketStatus) => {
    switch (ticketStatus) {
      case 'aberto':
        return 'danger';
      case 'em_atendimento':
        return 'warning';
      case 'pausado':
        return 'primary';
      case 'fechado':
        return 'success';
      case 'cancelado':
        return 'dark';
      default:
        return 'light';
    }
  };

  // ✅ FUNÇÃO PARA OBTER NOME AMIGÁVEL DO STATUS
  const getStatusDisplayName = (ticketStatus) => {
    switch (ticketStatus) {
      case 'aberto':
        return 'Aberto';
      case 'em_atendimento':
        return 'Em Atendimento';
      case 'pausado':
        return 'Pausado';
      case 'fechado':
        return 'Fechado';
      case 'cancelado':
        return 'Cancelado';
      default:
        return ticketStatus;
    }
  };

  // Atendimento por WhatsApp exige o protocolo do chat — é o que liga o ticket
  // à conversa na auditoria. Nas outras origens o campo nem aparece.
  const exigeProtocolo = String(origem || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .includes('whatsapp');

  // Preenche origem e protocolo quando a tela foi aberta pelo aviso do chat.
  // Empresa e solicitante só entram depois da mesa de serviço, mais abaixo.
  useEffect(() => {
    if (!chatPrefill) return;

    setProtocolNumber(chatPrefill.protocolNumber || '');

    // Usa o nome exato já cadastrado como origem, se existir, para não criar
    // uma variação nova ("WhatsApp" convivendo com "Whatsapp").
    const origemCadastrada = origensOptions.find((item) =>
      String(item.name || '')
        .toLowerCase()
        .replace(/[^a-z]/g, '')
        .includes('whatsapp')
    );
    setOrigem(origemCadastrada ? origemCadastrada.name : 'WhatsApp');
  }, [chatPrefill, origensOptions]);

  // ✅ EFEITO PARA CARREGAR DADOS INICIAIS (NÃO DEPENDENTES)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [serviceDesksRes, templatesRes, origensRes] = await Promise.all([
          axios.get('/servicedesks/list'),
          axios.get('/templates'),
          axios.get('/ticket-origins'),
        ]);
        setServiceDesks(serviceDesksRes.data || []);
        setEmailTemplates(templatesRes.data || []);
        setOrigensOptions(origensRes.data || []);
      } catch (err) {
        console.error('Erro ao carregar dados iniciais:', err);
        notification('Falha ao carregar dados para o formulário.', 'error');
      } finally {
        setDataLoaded(true);
      }
    };
    loadInitialData();
  }, [notification]);

  useEffect(() => {
    if (!selectedRequesterId) {
      setEmailValidation({ status: 'idle', message: '' });
      return;
    }
    const requester = filteredRequesters.find(
      (r) =>
        r.id === selectedRequesterId ||
        String(r.id) === String(selectedRequesterId)
    );
    if (!requester?.email) {
      setEmailValidation({ status: 'idle', message: '' });
      return;
    }
    setEmailValidation({ status: 'loading', message: 'Verificando email...' });
    axios
      .get(
        `/requesters/validate-email?email=${encodeURIComponent(requester.email)}`
      )
      .then(({ data }) => {
        if (data.valid === true) {
          setEmailValidation({ status: 'valid', message: data.message });
        } else if (data.valid === false) {
          setEmailValidation({ status: 'invalid', message: data.message });
        } else {
          setEmailValidation({ status: 'unverifiable', message: data.message });
        }
      })
      .catch(() => {
        setEmailValidation({ status: 'idle', message: '' });
      });
  }, [selectedRequesterId, filteredRequesters]);

  // HANDLERS
  // Definido antes de handleServiceDeskChange porque é chamado por ele quando
  // a tela vem preenchida pelo aviso do chat.
  const handleCompanyChange = async (value) => {
    const compId = value;
    setSelectedCompany(compId);
    setSelectedRequesterId(''); // Reseta o solicitante
    setFilteredRequesters([]);
    checkCustomerBlocked(compId); // ✅ Verificação automática ao selecionar empresa

    // ✅ BUSCA TICKETS DA EMPRESA SELECIONADA
    if (compId) {
      const selectedCompanyData = filteredCompanies.find(
        (c) => c.id === compId
      );
      const companyName =
        selectedCompanyData?.displayName ||
        selectedCompanyData?.nomeEmpresa ||
        selectedCompanyData?.razaoSocial ||
        'Empresa Selecionada';
      setSelectedCompanyName(companyName);
      fetchCustomerTickets(compId);
    } else {
      setSelectedCompanyName('');
      setCustomerTickets([]);
    }

    try {
      // Usa o novo endpoint que busca todos os requesters de uma empresa específica
      const { data: requesters } = await axios.get(
        `/requesters/company/${compId}`
      );
      setFilteredRequesters(requesters || []);
    } catch (err) {
      console.error('Erro ao buscar solicitantes:', err);
    }
  };

  const handleServiceDeskChange = async (value) => {
    const sdId = value;
    setServiceDesk(sdId);
    // Reseta todos os campos dependentes ao trocar a mesa
    setSelectedCompany('');
    setSelectedRequesterId('');
    setSelectedDepartment('');
    setSelectedUserId('');
    setPriority('');
    setStages('');
    setFilteredCompanies([]);
    setFilteredRequesters([]);
    setFilteredDepartments([]);
    setFilteredUsers([]);
    setFilteredPriorities([]);
    setFilteredStages([]);
    // ✅ LIMPA TICKETS AO TROCAR MESA
    setCustomerTickets([]);
    setSelectedCompanyName('');

    if (!sdId) return;

    try {
      // Buscar dados gerais do servicedesk
      const { data: deskDetails } = await axios.get(`/servicedesks/${sdId}`);
      setFilteredDepartments(deskDetails.departments || []);
      setFilteredPriorities(deskDetails.priorities || []);
      setFilteredStages(deskDetails.stages || []);

      // Buscar empresas do servicedesk
      const { data: customersData } = await axios.get(
        `/servicedesks/${sdId}/customers`
      );
      const onlyActive = Array.isArray(customersData)
        ? customersData.filter((c) => c.active !== false)
        : [];
      setFilteredCompanies(onlyActive);

      // Veio do chat: a empresa e o solicitante já identificados pelo telefone
      // entram sozinhos, desde que a empresa pertença à mesa escolhida.
      if (chatPrefill && chatPrefill.companyId) {
        const empresaNaMesa = onlyActive.find(
          (c) => String(c.id) === String(chatPrefill.companyId)
        );

        if (empresaNaMesa) {
          await handleCompanyChange(empresaNaMesa.id);
          if (chatPrefill.requesterId) {
            setSelectedRequesterId(chatPrefill.requesterId);
          }
        } else {
          notification(
            'A empresa do contato do chat não está vinculada a esta mesa de serviço.',
            'warning'
          );
        }
      }
    } catch (err) {
      console.error(`Erro ao buscar dados da mesa ${sdId}:`, err);
    }
  };

  // Vindo do chat, abre já na primeira mesa de serviço da empresa do
  // solicitante — é o que dispara o preenchimento de empresa e solicitante.
  // O agente continua livre para trocar a mesa e a empresa depois: trocar a
  // mesa apenas refaz este mesmo caminho para a mesa escolhida.
  useEffect(() => {
    if (!chatPrefill || !chatPrefill.companyId) return;
    if (mesaPrefillAplicada.current) return;
    mesaPrefillAplicada.current = true;

    const selecionarMesa = async () => {
      // Mesa já decidida no modal do chat: usa direto, sem nova consulta.
      if (chatPrefill.serviceDeskId) {
        await handleServiceDeskChange(chatPrefill.serviceDeskId);
        return;
      }

      // Sem escolha no modal (aviso antigo, por exemplo): cai na primeira mesa
      // da empresa, e o agente troca no campo se for outra.
      try {
        const { data } = await axios.get(
          `/servicedesks/customer/${chatPrefill.companyId}`
        );
        const mesas = Array.isArray(data) ? data : [];

        if (mesas.length === 0) {
          notification(
            'A empresa do contato do chat não está vinculada a nenhuma mesa de serviço. Selecione a mesa manualmente.',
            'warning'
          );
          return;
        }

        await handleServiceDeskChange(mesas[0].id);

        if (mesas.length > 1) {
          notification(
            `Aberto na mesa "${mesas[0].displayName || mesas[0].name}". Este cliente está em ${mesas.length} mesas — troque no campo acima se for outra.`,
            'info'
          );
        }
      } catch (err) {
        console.error('Erro ao buscar mesas da empresa do contato:', err);
        notification(
          'Não foi possível carregar a mesa de serviço do contato. Selecione manualmente.',
          'warning'
        );
      }
    };

    selecionarMesa();
  }, [chatPrefill]);

  const handleDepartmentChange = async (value) => {
    const deptoId = value;
    setSelectedDepartment(deptoId);
    setSelectedUserId(''); // Reseta o usuário
    setFilteredUsers([]);

    if (!deptoId) return;

    try {
      const { data: usersInDept } = await axios.get(`/userdpto/${deptoId}`);
      const mappedUsers = usersInDept.map((entry) => ({
        id: entry.UserProfile.User.id, // Usar o user_id real ao invés do userId do DepartamentUser
        name: entry.UserProfile.name,
        lastname: entry.UserProfile.lastname,
      }));
      setFilteredUsers(mappedUsers);
    } catch (err) {
      console.error('Erro ao buscar usuários pelo departamento:', err);
    }
  };

  const handleTemplateChange = async (e) => {
    const tplId = e.target.value;
    setSelectedTemplateId(tplId);
    if (!tplId) {
      setInternalCommunication('');
      return;
    }
    try {
      const { data: template } = await axios.get(`/templates/${tplId}`);
      setInternalCommunication(template.content || '');
    } catch (err) {
      console.error('Erro ao buscar conteúdo do template:', err);
    }
  };

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  // Handlers para o modal de solicitante
  const handleOpenRequesterModal = () => {
    if (!selectedCompany) {
      notification('Selecione uma empresa primeiro', 'warning');
      return;
    }
    setShowRequesterModal(true);
  };

  const handleCloseRequesterModal = () => {
    setShowRequesterModal(false);
  };

  const handleRequesterCreated = async (newRequester) => {
    // Atualiza a lista de solicitantes com o novo solicitante
    setFilteredRequesters((prev) => [...prev, newRequester]);
    // Seleciona automaticamente o novo solicitante
    setSelectedRequesterId(newRequester.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailValidation.status === 'invalid') {
      notification(emailValidation.message, 'error');
      return;
    }

    // O SearchableSelect não é um <input>, então o required do HTML não vale
    // aqui — a checagem precisa ser explícita.
    if (!String(origem || '').trim()) {
      notification('Selecione a origem do ticket.', 'error');
      return;
    }

    if (exigeProtocolo && !protocolNumber.trim()) {
      notification(
        'Informe o protocolo do chat para tickets com origem WhatsApp.',
        'error'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const ticketData = {
        assignedTo: selectedUserId,
        requesterId: selectedRequesterId,
        priority,
        serviceDesk,
        status,
        descricao_ticket: internalCommunication || '',
        internalCommunication: '',
        clientCommunication: '',
        company: selectedCompany,
        department: selectedDepartment,
        title,
        templateId: selectedTemplateId || null,
        stageid: stages,
        origem: origem || null,
        protocol_number: protocolNumber.trim() || null,
      };

      const formData = new FormData();
      formData.append('ticketData', JSON.stringify(ticketData));
      files.forEach((file) => {
        formData.append('files', file);
      });

      await axios.post('/tickets', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      notification('Ticket criado com sucesso!', 'success');
      navigate('/tickets');
    } catch (err) {
      console.error('Falha ao enviar ticket:', err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        'Erro ao enviar os dados do ticket.';
      notification(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!dataLoaded) {
    return <Loading />;
  }

  // ✅ RENDERIZAR PAINEL LATERAL DE TICKETS
  const renderTicketsSidebar = () => {
    if (!selectedCompany) return null;

    const ticketGroups = groupTicketsByStatus();
    const totalTickets = Array.isArray(customerTickets)
      ? customerTickets.length
      : 0;

    return (
      <Card className="h-100">
        <Card.Header className="bg-light">
          <h6 className="mb-1">
            <strong>Tickets - {selectedCompanyName}</strong>
            <Badge bg="primary" className="ms-2">
              {totalTickets}
            </Badge>
          </h6>
          {selectedCompanyTags.length > 0 && (
            <div className="mt-1">
              {selectedCompanyTags.map((tag) => {
                const name = String(tag?.name || '');
                const isBlocked = name.toLowerCase().includes('bloqueado');
                const style = tag?.color
                  ? { backgroundColor: tag.color, color: '#fff' }
                  : undefined;
                return (
                  <Badge
                    key={tag.id || name}
                    bg={isBlocked ? 'danger' : 'secondary'}
                    className="me-1"
                    style={style}
                  >
                    {name}
                  </Badge>
                );
              })}
            </div>
          )}
        </Card.Header>
        <Card.Body
          className="p-0"
          style={{ maxHeight: '600px', overflowY: 'auto' }}
        >
          {loadingTickets ? (
            <div className="text-center p-3">
              <Spinner animation="border" size="sm" />
              <div className="mt-2">Carregando tickets...</div>
            </div>
          ) : totalTickets === 0 ? (
            <div className="text-center p-3 text-muted">
              Nenhum ticket encontrado para esta empresa
            </div>
          ) : (
            Object.entries(ticketGroups).map(([ticketStatus, tickets]) => {
              if (tickets.length === 0) return null;

              return (
                <div key={ticketStatus} className="mb-3">
                  <div className="px-3 py-2 bg-light border-bottom">
                    <strong className="text-uppercase small">
                      {getStatusDisplayName(ticketStatus)}
                      <Badge
                        bg={getStatusBadgeVariant(ticketStatus)}
                        className="ms-2"
                      >
                        {tickets.length}
                      </Badge>
                    </strong>
                  </div>
                  <ListGroup variant="flush">
                    {tickets.map((ticket) => (
                      <ListGroup.Item
                        key={ticket.id}
                        className="px-3 py-2 border-0"
                        style={{ fontSize: '0.9rem' }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="fw-bold text-primary">
                              #{ticket.id}
                            </div>
                            <div className="text-truncate" title={ticket.title}>
                              {ticket.title}
                            </div>
                            {ticket.AssignedUser && (
                              <small className="text-muted">
                                {ticket.AssignedUser.UserProfile?.name ||
                                  ticket.AssignedUser.UserProfile?.nome ||
                                  'Usuário Atribuído'}
                              </small>
                            )}
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              window.open(
                                `/tickets/${ticket.id}/edit`,
                                '_blank'
                              )
                            }
                            title="Abrir ticket em nova aba"
                            className="ms-2"
                          >
                            <FaExternalLinkAlt size={12} />
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              );
            })
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Container fluid className="px-lg-4 px-xl-15">
      {/* ✅ Alerta fixo no canto superior direito */}
      <BlockedAlert
        show={isCustomerBlocked}
        message="Esse cliente encontra-se bloqueado, por favor verifique com o financeiro"
      />
      <Breadcrumbs
        title="Novo Ticket"
        pages={[{ name: 'Tickets', link: '/tickets' }]}
      />

      <Row>
        {/* ✅ COLUNA PRINCIPAL DO FORMULÁRIO */}
        <Col lg={selectedCompany ? 8 : 12}>
          <Card>
            <Card.Header>
              <Form.Group className="mb-3" style={{ height: '250px' }}>
                <Form.Label>
                  <strong>Descrição</strong>
                </Form.Label>
                <ReactQuill
                  ref={internalQuillRef}
                  theme="snow"
                  value={internalCommunication}
                  onChange={setInternalCommunication}
                  style={{ height: '200px' }}
                />
              </Form.Group>
            </Card.Header>

            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Label>Título</Form.Label>
                      <Form.Control
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Modelo de Mensagem</Form.Label>
                      <Form.Select
                        value={selectedTemplateId}
                        onChange={handleTemplateChange}
                      >
                        <option value="">--Selecione--</option>
                        {emailTemplates.map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>
                            {tpl.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Origem do Ticket *</Form.Label>
                      <SearchableSelect
                        options={origensOptions}
                        value={origem}
                        onChange={(val) => setOrigem(val)}
                        onInputChange={(val) => setOrigem(val)}
                        placeholder="Selecione ou crie uma origem..."
                        displayKey="name"
                        valueKey="name"
                        allowCreate
                        onCreateOption={async (newVal) => {
                          try {
                            const { data } = await axios.post(
                              '/ticket-origins',
                              { name: newVal }
                            );
                            setOrigensOptions((prev) => [...prev, data]);
                            setOrigem(data.name);
                          } catch (_) {
                            setOrigem(newVal);
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                  {exigeProtocolo && (
                    <Col>
                      <Form.Group>
                        <Form.Label>Protocolo do chat *</Form.Label>
                        <Form.Control
                          type="text"
                          value={protocolNumber}
                          onChange={(e) => setProtocolNumber(e.target.value)}
                          placeholder="PROT-00000000-000000"
                          required
                        />
                        <Form.Text className="text-muted">
                          Protocolo do atendimento no chat, usado depois para
                          auditar o ticket.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Label>Mesa de serviço</Form.Label>
                      <SearchableSelect
                        options={serviceDesks}
                        value={serviceDesk}
                        onChange={handleServiceDeskChange}
                        placeholder="Digite para pesquisar mesa de serviço..."
                        displayKey="displayName"
                        valueKey="id"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Empresa</Form.Label>
                      <SearchableSelect
                        options={filteredCompanies}
                        value={selectedCompany}
                        onChange={handleCompanyChange}
                        placeholder="Digite para pesquisar empresa..."
                        displayKey={(company) => {
                          return (
                            company.displayName ||
                            company.nomeEmpresa ||
                            company.razaoSocial
                          );
                        }}
                        valueKey="id"
                        disabled={!serviceDesk}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Solicitante</Form.Label>
                      <div className="d-flex gap-2">
                        <div className="flex-grow-1">
                          <SearchableSelect
                            options={filteredRequesters}
                            value={selectedRequesterId}
                            onChange={(value) => setSelectedRequesterId(value)}
                            placeholder="Digite para pesquisar solicitante..."
                            displayKey={(req) => `${req.name} – ${req.email}`}
                            valueKey="id"
                            disabled={!selectedCompany}
                            required
                          />
                        </div>
                        <Button
                          variant="outline-primary"
                          onClick={handleOpenRequesterModal}
                          disabled={!selectedCompany}
                          title="Criar novo solicitante"
                          style={{ minWidth: '45px' }}
                        >
                          <FaPlus />
                        </Button>
                      </div>
                      {emailValidation.status === 'loading' && (
                        <small className="text-muted mt-1 d-block">
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                          Verificando email...
                        </small>
                      )}
                      {emailValidation.status === 'valid' && (
                        <small className="text-success mt-1 d-block">
                          ✔ Email verificado com sucesso.
                        </small>
                      )}
                      {emailValidation.status === 'invalid' && (
                        <small className="text-danger mt-1 d-block">
                          ✖ {emailValidation.message}
                        </small>
                      )}
                      {emailValidation.status === 'unverifiable' && (
                        <small className="text-warning mt-1 d-block">
                          ⚠ {emailValidation.message}
                        </small>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Label>Departamento</Form.Label>
                      <SearchableSelect
                        options={filteredDepartments}
                        value={selectedDepartment}
                        onChange={handleDepartmentChange}
                        placeholder="Digite para pesquisar departamento..."
                        displayKey="name"
                        valueKey="id"
                        disabled={!serviceDesk}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Prioridade</Form.Label>
                      <SearchableSelect
                        options={filteredPriorities}
                        value={priority}
                        onChange={(value) => setPriority(value)}
                        placeholder="Digite para pesquisar prioridade..."
                        displayKey="name"
                        valueKey="id"
                        disabled={!serviceDesk}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Estágio</Form.Label>
                      <SearchableSelect
                        options={filteredStages}
                        value={stages}
                        onChange={(value) => setStages(value)}
                        placeholder="Digite para pesquisar estágio..."
                        displayKey="name"
                        valueKey="id"
                        disabled={!serviceDesk}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Label>Responsável</Form.Label>
                      <SearchableSelect
                        options={filteredUsers}
                        value={selectedUserId}
                        onChange={(value) => setSelectedUserId(value)}
                        placeholder="Digite para pesquisar responsável..."
                        displayKey={(user) => `${user.name} ${user.lastname}`}
                        valueKey="id"
                        disabled={!selectedDepartment}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        disabled
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="aberto">Aberto</option>
                        <option value="em_atendimento">Em Atendimento</option>
                        <option value="pausado">Pausado</option>
                        <option value="fechado">Fechado</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3 d-flex justify-content-end">
                  <Col>
                    <Form.Group>
                      <Form.Label>Arquivos</Form.Label>
                      <Form.Control
                        type="file"
                        multiple
                        ref={fileInputRef}
                        onChange={handleFileChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs="auto" className="d-flex align-items-end">
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Abrir'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* ✅ COLUNA DO PAINEL LATERAL DE TICKETS */}
        {selectedCompany && <Col lg={4}>{renderTicketsSidebar()}</Col>}
      </Row>

      {/* Modal para criar novo solicitante */}
      <RequesterModal
        show={showRequesterModal}
        onHide={handleCloseRequesterModal}
        selectedCompanyId={selectedCompany}
        onRequesterCreated={handleRequesterCreated}
      />
    </Container>
  );
}
